import { useQuery } from '@tanstack/react-query'
import { Link, useSearch } from '@tanstack/react-router'

import type { SystemLogEntry } from '@/bindings/SystemLogEntry'
import { Badge } from '@/components/badge'
import { Button } from '@/components/button'
import { FormError } from '@/components/form-error'
import { Card, Page } from '@/components/layout'
import { Pager } from '@/components/pager'
import { type LogLevelFilter, fieldText, systemLogListQuery } from '@/features/system-log/api'
import { cx } from '@/lib/cx'
import { useT } from '@/lib/i18n/translate'
import { useClampedPage } from '@/lib/paging'

/**
 * The system log (H4): every warning and error the server logged, read here
 * so a developer can fix things without cloud-console access. Read-only.
 */
export function SystemLogPage() {
  const t = useT()
  const { page, level } = useSearch({ from: '/platform-auth/system-log' })
  const list = useQuery(systemLogListQuery(level, page))
  const link = (page: number) => ({ to: '/system-log', search: { page, level } }) as const
  useClampedPage(page, list.data, link)

  const total = list.data?.total ?? 0

  return (
    <Page width="lg" title={t('systemLog.title')} className="flex flex-col gap-4">
      <p className="text-muted text-sm">{t('systemLog.intro')}</p>
      <nav aria-label={t('systemLog.tabs')} className="flex gap-2">
        <Tab level="error" current={level}>
          {t('systemLog.errors')}
        </Tab>
        <Tab level="all" current={level}>
          {t('systemLog.all')}
        </Tab>
      </nav>

      {list.isPending && <p className="text-muted text-sm">{t('common.loading')}</p>}
      {list.error && (
        <div className="flex flex-col gap-2">
          <FormError error={list.error} />
          <Button variant="secondary" onClick={() => void list.refetch()} className="self-start">
            {t('common.retry')}
          </Button>
        </div>
      )}

      {list.data &&
        (list.data.items.length === 0 ? (
          <Card>
            <p className="text-muted text-sm">{t(level === 'error' ? 'systemLog.emptyErrors' : 'systemLog.emptyAll')}</p>
          </Card>
        ) : (
          <ul className="flex flex-col gap-2">
            {list.data.items.map((entry) => (
              <li key={entry.id}>
                <EntryRow entry={entry} />
              </li>
            ))}
          </ul>
        ))}

      <Pager label={t('systemLog.pager')} page={page} total={total} link={link} />
    </Page>
  )
}

function Tab({ level, current, children }: { level: LogLevelFilter; current: LogLevelFilter; children: string }) {
  const active = level === current
  return (
    <Link
      to="/system-log"
      search={{ page: 1, level }}
      aria-current={active ? 'page' : undefined}
      className={cx(
        'flex min-h-tap items-center rounded-lg border px-4 font-medium',
        active ? 'border-accent bg-accent-soft text-accent-ink' : 'border-border bg-surface hover:bg-accent-soft',
      )}
    >
      {children}
    </Link>
  )
}

function EntryRow({ entry }: { entry: SystemLogEntry }) {
  const t = useT()
  const fields = Object.entries(entry.fields)
  return (
    <Card className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Badge tone={entry.level === 'error' ? 'danger' : 'warning'}>{t(`systemLog.level.${entry.level}`)}</Badge>
        <time dateTime={entry.created_at} className="text-muted">
          {new Date(entry.created_at).toLocaleString()}
        </time>
        <span className="text-muted truncate font-mono text-xs">{entry.target}</span>
      </div>
      <p className="font-medium wrap-break-word">{entry.message}</p>
      {fields.length > 0 && (
        <dl aria-label={t('systemLog.details')} className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
          {fields.map(([key, value]) => (
            <div key={key} className="contents">
              <dt className="text-muted font-mono text-xs leading-5">{key}</dt>
              <dd className="font-mono text-xs leading-5 break-all">{fieldText(value)}</dd>
            </div>
          ))}
        </dl>
      )}
    </Card>
  )
}
